import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, obj = auth
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("alumnios_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("alumnios_token");
        }
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    setError("");
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("alumnios_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithToken = async (token) => {
    localStorage.setItem("alumnios_token", token);
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  // Auto sign-out after 30 minutes of inactivity.
  useEffect(() => {
    if (!user) return;
    const TIMEOUT = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        localStorage.removeItem("alumnios_token");
        localStorage.removeItem("ud_admin_token");
        setUser(false);
        try { window.location.assign("/login?timeout=1"); } catch { /* noop */ }
      }, TIMEOUT);
    };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user]);

  const logout = () => {
    localStorage.removeItem("alumnios_token");
    localStorage.removeItem("ud_admin_token");
    setUser(false);
  };

  const impersonate = async (alumnusId) => {
    try { localStorage.setItem("ud_emulate_return", window.location.pathname + window.location.search); } catch { /* noop */ }
    const { data } = await api.post(`/admin/impersonate/${alumnusId}`);
    const adminToken = localStorage.getItem("alumnios_token");
    if (adminToken) localStorage.setItem("ud_admin_token", adminToken);
    localStorage.setItem("alumnios_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const impersonateUser = async (userId) => {
    try { localStorage.setItem("ud_emulate_return", window.location.pathname + window.location.search); } catch { /* noop */ }
    const { data } = await api.post(`/admin/impersonate-user/${userId}`);
    const adminToken = localStorage.getItem("alumnios_token");
    if (adminToken) localStorage.setItem("ud_admin_token", adminToken);
    localStorage.setItem("alumnios_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const stopImpersonation = () => {
    const adminToken = localStorage.getItem("ud_admin_token");
    if (adminToken) {
      localStorage.setItem("alumnios_token", adminToken);
      localStorage.removeItem("ud_admin_token");
    }
    return api.get("/auth/me").then((r) => { setUser(r.data); return r.data; });
  };

  const setUserSession = (u) => setUser(u);

  const isAdmin = user && (user.role === "admin" || user.role === "superadmin" || user.role === "alumni_manager");
  const isSuperadmin = user && user.role === "superadmin";
  const isFullAdmin = user && (user.role === "admin" || user.role === "superadmin");
  const isClassAgent = user && user.role === "class_agent";

  return (
    <AuthContext.Provider value={{ user, login, loginWithToken, logout, isAdmin, isSuperadmin, isFullAdmin, isClassAgent, error, setError, setUserSession, impersonate, impersonateUser, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
